import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, ConfirmDialog, Section, StateView, Technical, useAuth, useToast } from "@ozpanel/ui";
import { DataList, type DataListColumn } from "./DataList";
import { StatusPill } from "./PageHeader";
import { Field, NumberField } from "./Form";

// The client-certificate (mTLS) panel — the WHOLE of it, in ONE place.
//
// It used to live inline in the admin VhostDetail screen only, so a tenant configuring their own
// site had a bare `mtls_ca_path` text box and no way to create the authority or issue a
// certificate — the routes are all `domain_admin`, which a tenant IS, so nothing but the UI was
// missing (CLAUDE.md rule 8). Extracted here so the admin console and the account console render
// the exact same panel from the same code; a divergence between "what an operator sees" and "what
// the customer sees" for the same feature is how one of them silently rots.
//
// The four routes (all domain_admin):
//   GET  /caddy/vhosts/{d}/mtls          status + CA info + a COUNT of live certs
//   POST /caddy/vhosts/{d}/mtls/ca       generate the vhost's own CA (wires mtls_ca_path)
//   POST /caddy/vhosts/{d}/mtls/clients  issue a client cert — returns PEM + PKCS#12 ONCE
//   GET  /caddy/vhosts/{d}/mtls/clients  list issued certs (metadata only)

export interface MtlsStatus {
  enabled?: boolean;
  mode?: string;
  ca_path?: string;
  ca?: { subject?: string; fingerprint_sha256?: string; not_after?: string; path?: string; pem?: string } | null;
  clients?: number;
}

interface ClientCert {
  serial?: string;
  common_name?: string;
  email?: string;
  not_after?: string;
  revoked?: boolean;
  expired?: boolean;
  created_at?: string;
}

// Returned ONCE by the issue route. There is no endpoint that can show it again — the panel does
// not keep the private half — so it is held on screen until dismissed rather than toasted away.
interface Issued {
  cert_pem?: string;
  key_pem?: string;
  p12_base64?: string;
  p12_password?: string;
}

export function MtlsPanel({ domain }: { domain: string }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const enc = encodeURIComponent(domain);

  const mtls = useQuery({
    queryKey: ["vhost-mtls", domain],
    queryFn: () => client.apiFetch<MtlsStatus>(`/caddy/vhosts/${enc}/mtls`),
    retry: false,
  });
  // A SEPARATE endpoint from /mtls, which carries only the count. Reading the count as the array
  // (a `.map` on a number) is what took the admin tab down the moment any cert existed — kept
  // apart here so the two can never be confused.
  const clients = useQuery({
    queryKey: ["vhost-mtls-clients", domain],
    queryFn: () => client.apiFetch<{ items: ClientCert[] | null }>(`/caddy/vhosts/${enc}/mtls/clients`),
    retry: false,
  });

  const [makingCA, setMakingCA] = React.useState(false);
  const [caDays, setCaDays] = React.useState("3650");
  const [issuing, setIssuing] = React.useState<{ common_name: string; email: string; validity_days: string } | null>(null);
  const [issued, setIssued] = React.useState<Issued | null>(null);
  const [revoking, setRevoking] = React.useState<ClientCert | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["vhost-mtls", domain] });
    void qc.invalidateQueries({ queryKey: ["vhost-mtls-clients", domain] });
  };
  const fail = (e: unknown) => notify({ kind: "error", title: "That did not work", body: e instanceof Error ? e.message : String(e) });

  const hasCA = !!mtls.data?.ca_path;

  const clientCols: DataListColumn<ClientCert>[] = [
    { key: "common_name", label: "Name", sortable: true, render: (x) => x.common_name || <span className="text-muted">—</span> },
    { key: "email", label: "Email", render: (x) => x.email || <span className="text-muted">—</span> },
    { key: "not_after", label: "Expires", sortable: true, render: (x) => x.not_after || "—" },
    {
      key: "status",
      label: "Status",
      render: (x) =>
        x.revoked ? (
          <StatusPill tone="bad">revoked</StatusPill>
        ) : x.expired ? (
          <StatusPill tone="warn">expired</StatusPill>
        ) : (
          <StatusPill tone="ok">valid</StatusPill>
        ),
    },
  ];

  return (
    <Section
      className="mt-6"
      title="Client certificates"
      bodyClassName="p-0"
      actions={
        <span className="inline-flex items-center gap-2">
          {!hasCA ? (
            <Button size="sm" onClick={() => setMakingCA(true)}>Create the authority</Button>
          ) : (
            <Button size="sm" onClick={() => setIssuing({ common_name: "", email: "", validity_days: "365" })}>Issue a certificate</Button>
          )}
          <StatusPill tone={mtls.data?.enabled ? "ok" : "neutral"}>
            {mtls.data?.enabled ? mtls.data.mode ?? "on" : "off"}
          </StatusPill>
        </span>
      }
    >
      <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
        When required, a visitor must present a certificate this site&apos;s own authority issued —
        nothing else reaches the application, including a health check, which is why it is usually
        wrong to require on a public site. Turn the requirement on under Settings once at least one
        certificate has been issued; turning it on with none issued locks everybody out.
      </div>

      <StateView loading={clients.isLoading} error={clients.error} errorContext="client certificates">
        <div className="p-4">
          <DataList
            items={clients.data?.items ?? []}
            columns={clientCols}
            getRowKey={(x) => String(x.serial ?? x.common_name)}
            loading={clients.isLoading}
            emptyMessage={hasCA ? "No client certificates have been issued for this site." : "Create the authority first, then issue certificates."}
            rowActions={(x) =>
              x.revoked ? (
                <span className="text-[11.5px] text-muted">revoked</span>
              ) : (
                <Button size="sm" variant="danger" onClick={() => setRevoking(x)}>
                  Revoke
                </Button>
              )
            }
          />
        </div>
      </StateView>

      {/* Where the authority's file lives on the server is a technical detail (audit 2026-11-02). */}
      {mtls.data?.ca_path && (
        <Technical>
          <div className="border-t border-line px-4 py-2.5 font-mono text-[11.5px] text-muted">{mtls.data.ca_path}</div>
        </Technical>
      )}

      {/* Create the CA. Its lifetime is sent explicitly: left to the server's default a client CA
          expires ten years out with nothing recording that choice. */}
      <ConfirmDialog
        open={makingCA}
        onOpenChange={setMakingCA}
        title={`Create a client-certificate authority for ${domain}?`}
        confirmLabel="Create it"
        body={
          <div className="space-y-2">
            <p>
              Generates this site&apos;s own certificate authority and points the site at it. Only
              certificates issued by this authority will be accepted once client certificates are
              required — and requiring them before issuing any locks everybody out, health checks
              included.
            </p>
            <NumberField label="Authority valid for" unit="days" value={caDays} onChange={setCaDays} placeholder="3650" />
          </div>
        }
        onConfirm={() => {
          setMakingCA(false);
          void client
            .apiFetch(`/caddy/vhosts/${enc}/mtls/ca`, {
              method: "POST",
              body: JSON.stringify({ validity_days: Number(caDays) || 3650 }),
            })
            .then(() => { notify({ kind: "success", title: "Authority created" }); refresh(); })
            .catch(fail);
        }}
      />

      {/* Issue a client certificate. */}
      <ConfirmDialog
        open={!!issuing}
        onOpenChange={(o) => !o && setIssuing(null)}
        title="Issue a client certificate"
        confirmLabel="Issue"
        confirmDisabled={!issuing?.common_name.trim()}
        body={
          <div className="space-y-3">
            <Field
              label="Who it is for"
              mono
              value={issuing?.common_name ?? ""}
              placeholder="alice-laptop"
              onChange={(v) => setIssuing(issuing ? { ...issuing, common_name: v } : null)}
            />
            <Field
              label="Email (optional)"
              value={issuing?.email ?? ""}
              onChange={(v) => setIssuing(issuing ? { ...issuing, email: v } : null)}
            />
            <NumberField
              label="Valid for"
              unit="days"
              value={issuing?.validity_days ?? "365"}
              onChange={(v) => setIssuing(issuing ? { ...issuing, validity_days: v } : null)}
              placeholder="365"
            />
            <p className="text-[11.5px] leading-relaxed text-muted">
              The certificate and its password are shown once and cannot be retrieved again — the
              panel does not keep the private half.
            </p>
          </div>
        }
        onConfirm={() => {
          const i = issuing;
          setIssuing(null);
          if (!i?.common_name.trim()) return;
          void client
            .apiFetch<Issued>(`/caddy/vhosts/${enc}/mtls/clients`, {
              method: "POST",
              body: JSON.stringify({
                common_name: i.common_name.trim(),
                email: i.email.trim(),
                validity_days: Number(i.validity_days) || 365,
              }),
            })
            .then((r) => { setIssued(r); notify({ kind: "success", title: "Certificate issued" }); refresh(); })
            .catch(fail);
        }}
      />

      {/* Shown once. The handler returns cert_pem / key_pem / p12_base64 / p12_password; the
          private material is discarded server-side, so a wrongly-read key here would make every
          issued cert permanently unusable. */}
      <ConfirmDialog
        open={!!issued}
        onOpenChange={(o) => !o && setIssued(null)}
        title="Shown only now"
        confirmLabel="I have saved it"
        body={
          <div className="space-y-2">
            {issued?.p12_password && (
              <div>
                <div className="text-[12px] text-muted">Password for the .p12</div>
                <div className="break-all rounded border border-line bg-bg px-2 py-1 font-mono text-[13px]">{issued.p12_password}</div>
              </div>
            )}
            {issued?.p12_base64 && (
              <Button
                size="sm"
                onClick={() => {
                  const bin = atob(issued.p12_base64!);
                  const bytes = new Uint8Array(bin.length);
                  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                  const url = URL.createObjectURL(new Blob([bytes], { type: "application/x-pkcs12" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${domain}-client.p12`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download .p12
              </Button>
            )}
            {(issued?.cert_pem || issued?.key_pem) && (
              <textarea
                readOnly
                value={[issued.cert_pem, issued.key_pem].filter(Boolean).join("\n")}
                className="h-40 w-full rounded border border-line bg-bg p-2 font-mono text-[10.5px]"
              />
            )}
          </div>
        }
        onConfirm={() => setIssued(null)}
      />

      {/* Revoke. base Caddy has no client-cert CRL, so this drops the cert from the site's trusted-
          leaf allowlist and reloads Caddy — the cert is refused here at once and on every node
          within a gate pass, while every other certificate under this authority keeps working. */}
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Revoke ${revoking?.common_name || revoking?.serial}?`}
        confirmLabel="Revoke it"
        destructive
        body={
          <p>
            The holder of this certificate will be refused immediately — here now, and on every node
            within a moment. It cannot be un-revoked; issue a new certificate if they need access
            again. Nothing else under this authority is affected.
          </p>
        }
        onConfirm={() => {
          const c = revoking;
          setRevoking(null);
          if (!c?.serial) return;
          void client
            .apiFetch(`/caddy/vhosts/${enc}/mtls/clients/${encodeURIComponent(c.serial)}/revoke`, { method: "POST" })
            .then(() => { notify({ kind: "success", title: "Certificate revoked" }); refresh(); })
            .catch(fail);
        }}
      />
    </Section>
  );
}
